/***
*
*   PLAN CARD
*   Displays a single plan with its details and actions
*
**********/

import { Card, Badge, Button, Icon } from 'components/lib';
import Style from './bundlePackaging.tailwind.js';

export function PlanCard({ plan, onEdit, onDelete, onDuplicate }) {
  const isSystemPlan = plan.id === 'free' || plan.id === 'trial';
  
  const formatPrice = (price) => {
    if (price === null || price === undefined) return 'Custom';
    if (price === 0) return 'Free';
    return `$${price}`;
  };

  const getFeatureCount = () => {
    if (plan.features) {
      return Object.keys(plan.features).length;
    }
    if (plan.modules_enabled) {
      return plan.modules_enabled.length;
    }
    return 0;
  };

  const getPlanBadgeColor = () => {
    if (plan.id === 'free') return 'gray';
    if (plan.id === 'trial') return 'blue';
    if (plan.price === null) return 'purple';
    return 'green';
  };

  return (
    <Card className={Style.planCard}>
      {/* Badge */}
      <div className={Style.planBadge}>
        <Badge 
          text={isSystemPlan ? plan.id.toUpperCase() : 'PAID'} 
          color={getPlanBadgeColor()} 
        />
      </div>

      {/* Header */}
      <div className={Style.planCardHeader}>
        <div>
          <h3 className={Style.planName}>{plan.name}</h3>
          <div className="flex items-baseline gap-1 mt-2">
            <span className={Style.planPrice}>{formatPrice(plan.price)}</span>
            {plan.price > 0 && (
              <span className={Style.planInterval}>/{plan.interval || 'month'}</span>
            )}
          </div>
          {plan.discounted_price && (
            <div className="text-sm text-green-600 mt-1">
              Discounted: ${plan.discounted_price}/{plan.interval || 'month'}
            </div>
          )}
        </div>
      </div>

      {/* Features Summary */}
      <div className={Style.planFeatures}>
        <div className={Style.planFeature}>
          <Icon image="check" color="green" size={16} />
          <span>{getFeatureCount()} features enabled</span>
        </div>
        {plan.features_summary?.file_storage && (
          <div className={Style.planFeature}>
            <Icon image="hard-drive" color="blue" size={16} />
            <span>{plan.features_summary.file_storage} {plan.features_summary.file_storage_unit || 'MB'} storage</span>
          </div>
        )}
        {plan.features_summary?.manage_people && (
          <div className={Style.planFeature}>
            <Icon image="users" color="purple" size={16} />
            <span>{plan.features_summary.manage_people} employees</span>
          </div>
        )}
        {plan.features_summary?.hiring_recruitment && (
          <div className={Style.planFeature}>
            <Icon image="briefcase" color="orange" size={16} />
            <span>
              {plan.features_summary.hiring_recruitment === 'unlimited' 
                ? 'Unlimited' 
                : plan.features_summary.hiring_recruitment} job openings
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className={Style.planActions}>
        <Button
          text="Edit"
          icon="edit-2"
          size="small"
          color="blue"
          outline
          action={() => onEdit(plan)}
        />
        <Button
          text="Duplicate"
          icon="copy"
          size="small"
          color="gray"
          outline
          action={() => onDuplicate(plan)}
        />
        {!isSystemPlan && (
          <Button
            text="Delete"
            icon="trash-2"
            size="small"
            color="red"
            outline
            action={() => onDelete(plan)}
          />
        )}
      </div>
    </Card>
  );
}